import { Box, Cyl, Slab } from '../../render/parts'
import type { Tone } from '../../render/theme'

/** Carcasses, doors, shelves and handles — every storage item is built from these. */

export interface CarcassProps {
  width: number
  depth: number
  height: number
  tone?: Tone
  /** Recessed plinth at the bottom, as on real casegoods. */
  plinth?: number
  wall?: number
}

/** Open box carcass: two sides, a top, a bottom and a back. */
export function Carcass({ width, depth, height, tone = 'shell', plinth = 0.04, wall = 0.02 }: CarcassProps) {
  const bodyBottom = plinth
  const bodyHeight = height - plinth
  return (
    <group>
      {plinth > 0 ? (
        <Box tone="shellSoft" size={[width - 0.06, plinth, depth - 0.06]} position={[0, plinth / 2, 0]} />
      ) : null}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone={tone}
          size={[wall, bodyHeight, depth]}
          position={[side * (width / 2 - wall / 2), bodyBottom + bodyHeight / 2, 0]}
        />
      ))}
      <Box tone={tone} size={[width, wall, depth]} position={[0, height - wall / 2, 0]} />
      <Box tone={tone} size={[width, wall, depth]} position={[0, bodyBottom + wall / 2, 0]} />
      <Box
        tone="shellSoft"
        size={[width - wall * 2, bodyHeight - wall * 2, 0.012]}
        position={[0, bodyBottom + bodyHeight / 2, -depth / 2 + 0.006]}
      />
    </group>
  )
}

export interface ShelvesProps {
  width: number
  depth: number
  /** Y positions of each shelf, measured from the floor. */
  levels: readonly number[]
  tone?: Tone
  thickness?: number
  inset?: number
}

export function Shelves({ width, depth, levels, tone = 'shell', thickness = 0.018, inset = 0.02 }: ShelvesProps) {
  return (
    <group>
      {levels.map((y) => (
        <Box
          key={y}
          tone={tone}
          size={[width - inset * 2, thickness, depth - inset]}
          position={[0, y, -inset / 2]}
        />
      ))}
    </group>
  )
}

export type HandleStyle = 'bar' | 'edge' | 'knob' | 'none'

export interface DoorFrontsProps {
  width: number
  height: number
  /** Y of the door's centre. */
  y: number
  depth: number
  count?: number
  tone?: Tone
  accent?: string
  handle?: HandleStyle
  handleTone?: Tone
  /** Sliding doors overlap instead of splitting flush. */
  sliding?: boolean
  glass?: boolean
}

/** A run of door fronts with handles. Handles the sliding/hinged split. */
export function DoorFronts({
  width,
  height,
  y,
  depth,
  count = 2,
  tone = 'shell',
  accent,
  handle = 'bar',
  handleTone = 'metalDark',
  sliding = false,
  glass = false,
}: DoorFrontsProps) {
  const gap = sliding ? 0 : 0.006
  const leaf = (width - gap * (count + 1)) / count
  const faceZ = depth / 2 + (sliding ? 0.012 : 0.008)

  return (
    <group>
      {Array.from({ length: count }, (_, index) => {
        const x = -width / 2 + gap + leaf / 2 + index * (leaf + gap)
        const z = sliding && index % 2 === 1 ? faceZ + 0.014 : faceZ
        return (
          <group key={index}>
            <Slab
              tone={glass ? 'glass' : tone}
              color={glass ? undefined : accent}
              size={[leaf, height, 0.014]}
              radius={0.006}
              rotation={[Math.PI / 2, 0, 0]}
              position={[x, y, z]}
            />
            {handle === 'bar' ? (
              <Box
                tone={handleTone}
                size={[0.014, height * 0.42, 0.016]}
                position={[x + (index % 2 === 0 ? leaf / 2 - 0.05 : -leaf / 2 + 0.05), y, z + 0.014]}
              />
            ) : null}
            {handle === 'knob' ? (
              <Cyl
                tone={handleTone}
                axis="z"
                r={0.014}
                h={0.024}
                segments={14}
                position={[x + (index % 2 === 0 ? leaf / 2 - 0.06 : -leaf / 2 + 0.06), y, z + 0.017]}
              />
            ) : null}
            {handle === 'edge' ? (
              <Box tone={handleTone} size={[leaf * 0.5, 0.01, 0.014]} position={[x, y + height / 2 - 0.03, z + 0.012]} />
            ) : null}
          </group>
        )
      })}
    </group>
  )
}

export interface DrawerFrontsProps {
  width: number
  depth: number
  /** Y of the bottom of the drawer stack. */
  bottom: number
  /** Y of the top of the drawer stack. */
  top: number
  count?: number
  tone?: Tone
  handleTone?: Tone
  accent?: string
  accentIndex?: number
}

export function DrawerFronts({
  width,
  depth,
  bottom,
  top,
  count = 3,
  tone = 'shellSoft',
  handleTone = 'metalDark',
  accent,
  accentIndex = -1,
}: DrawerFrontsProps) {
  const gap = 0.008
  const leaf = (top - bottom - gap * (count + 1)) / count
  return (
    <group>
      {Array.from({ length: count }, (_, index) => {
        const y = bottom + gap + leaf / 2 + index * (leaf + gap)
        const isAccent = index === accentIndex
        return (
          <group key={index}>
            <Slab
              tone={isAccent ? 'accent' : tone}
              color={isAccent ? accent : undefined}
              size={[width - 0.02, leaf, 0.014]}
              radius={0.006}
              rotation={[Math.PI / 2, 0, 0]}
              position={[0, y, depth / 2 + 0.008]}
            />
            <Box tone={handleTone} size={[width * 0.4, 0.011, 0.015]} position={[0, y + leaf / 2 - 0.032, depth / 2 + 0.018]} />
          </group>
        )
      })}
    </group>
  )
}

export interface BinderRowProps {
  width: number
  y: number
  z?: number
  height?: number
  accent?: string
  /** Deterministic seed so a shelf always looks the same across renders. */
  seed?: number
}

/** A row of ring binders — the cheapest way to make shelving read as "used". */
export function BinderRow({ width, y, z = 0, height = 0.28, accent, seed = 1 }: BinderRowProps) {
  const count = Math.max(3, Math.floor(width / 0.055))
  let cursor = -width / 2 + 0.02
  const items: { x: number; w: number; h: number; accented: boolean }[] = []
  for (let index = 0; index < count; index += 1) {
    const wobble = ((index * 37 + seed * 13) % 7) / 7
    const w = 0.032 + wobble * 0.022
    const h = height * (0.82 + wobble * 0.18)
    if (cursor + w > width / 2 - 0.02) break
    items.push({ x: cursor + w / 2, w, h, accented: (index * 5 + seed) % 6 === 0 })
    cursor += w + 0.004
  }
  return (
    <group>
      {items.map((item) => (
        <Box
          key={item.x}
          tone={item.accented ? 'accent' : 'shellSoft'}
          color={item.accented ? accent : undefined}
          size={[item.w, item.h, 0.24]}
          position={[item.x, y + item.h / 2, z]}
        />
      ))}
    </group>
  )
}
